import React, { useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { getSummary, listJobs } from "./aiHireApi";
import { LOCATIONS } from "./aiHireMockData";
import {
  ChipGroup,
  EmptyRow,
  FilterSelect,
  PreviewButton,
  SummaryStrip,
  Tone,
  Toolbar,
  fmtDate,
  useAiHireData,
} from "./AiHireUi";

const GROUPS = [
  { id: "project", label: "Project" },
  { id: "client", label: "Client" },
  { id: "owner", label: "Owner / PM" },
];

const JOB_STATUS_TONE = { Open: "green", "On hold": "amber", Closed: "gray" };

function groupHeading(groupBy, job) {
  if (groupBy === "client") return { title: job.client, meta: "" };
  if (groupBy === "owner") return { title: `Owner: ${job.owner}`, meta: `Hiring manager: ${job.hiringManager}` };
  return {
    title: `${job.client} > ${job.project}`,
    meta: `Owner: ${job.owner}  ·  Hiring manager: ${job.hiringManager}`,
  };
}

function groupKey(groupBy, job) {
  if (groupBy === "client") return job.client;
  if (groupBy === "owner") return `${job.owner}|${job.hiringManager}`;
  return `${job.client}|${job.project}`;
}

/** Job Postings: every job tied to a client, project, owner and hiring manager (project manager). */
function JobPostingsTab({ onOpenJob }) {
  const jobs = useAiHireData(listJobs, []);
  const summary = useAiHireData(getSummary, []);
  const [groupBy, setGroupBy] = useState("project");
  const [status, setStatus] = useState("");
  const [location, setLocation] = useState("");

  const groups = useMemo(() => {
    const out = new Map();
    (jobs || [])
      .filter((j) => (!status || j.status === status) && (!location || j.location === location))
      .forEach((j) => {
        const key = groupKey(groupBy, j);
        if (!out.has(key)) out.set(key, { heading: groupHeading(groupBy, j), jobs: [] });
        out.get(key).jobs.push(j);
      });
    return [...out.values()];
  }, [jobs, groupBy, status, location]);

  return (
    <div className="aihire-tab">
      {summary ? (
        <SummaryStrip
          items={[
            { label: "Open jobs", value: summary.openJobs },
            { label: "Applicants", value: summary.applicants },
            { label: "AI shortlisted", value: summary.aiShortlisted },
            { label: "Interviews this week", value: summary.interviewsThisWeek },
          ]}
        />
      ) : null}

      <Toolbar>
        <ChipGroup label="Group by" options={GROUPS} value={groupBy} onChange={setGroupBy} />
        <FilterSelect label="Status" value={status} onChange={setStatus} options={["Open", "On hold", "Closed"]} />
        <FilterSelect label="Location" value={location} onChange={setLocation} options={LOCATIONS} />
        <span className="aihire-toolbar__spacer" />
        <PreviewButton icon={FaPlus} primary>
          New job
        </PreviewButton>
      </Toolbar>

      {jobs && groups.length === 0 ? <p className="aihire-empty">No jobs match these filters.</p> : null}

      {groups.map((g) => (
        <section key={g.heading.title + g.heading.meta} className="aihire-group">
          <header className="aihire-group__head">
            <h3>{g.heading.title}</h3>
            {g.heading.meta ? <span>{g.heading.meta}</span> : null}
          </header>
          <div className="aihire-table-wrap">
            <table className="aihire-table">
              <thead>
                <tr>
                  <th>Job title</th>
                  {groupBy !== "project" ? <th>Client / Project</th> : null}
                  <th>Location</th>
                  <th>Mode</th>
                  <th className="num">Openings</th>
                  <th title="Applied > Shortlisted > Interview > Offer">Pipeline</th>
                  <th>Status</th>
                  <th>Posted</th>
                </tr>
              </thead>
              <tbody>
                {g.jobs.length === 0 ? <EmptyRow colSpan={8}>No jobs</EmptyRow> : null}
                {g.jobs.map((j) => (
                  <tr key={j.id} className="is-clickable" onClick={() => onOpenJob(j.id)} title="Open candidates">
                    <td>
                      <button type="button" className="aihire-link">
                        {j.title}
                      </button>
                    </td>
                    {groupBy !== "project" ? (
                      <td>
                        {j.client} / {j.project}
                      </td>
                    ) : null}
                    <td>{j.location}</td>
                    <td>{j.workMode}</td>
                    <td className="num">{j.openings}</td>
                    <td>
                      <span className="aihire-pipeline" aria-label="Applied, shortlisted, interview, offer">
                        <b>{j.pipeline.applied}</b> applied <i>&rsaquo;</i> <b>{j.pipeline.shortlisted}</b> shortlisted{" "}
                        <i>&rsaquo;</i> <b>{j.pipeline.interview}</b> interview <i>&rsaquo;</i>{" "}
                        <b>{j.pipeline.offer}</b> offer
                      </span>
                    </td>
                    <td>
                      <Tone tone={JOB_STATUS_TONE[j.status]}>{j.status}</Tone>
                    </td>
                    <td>{fmtDate(j.postedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

export default JobPostingsTab;
